import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  DatePicker,
  Empty,
  Input,
  List,
  message,
  Modal,
  Radio,
  Rate,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  AudioOutlined,
  CopyOutlined,
  DownloadOutlined,
  StopOutlined,
  EditOutlined,
  SaveOutlined,
  WechatOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isEmpty from "../../validations/is-empty";
import openNotification from "../../components/Notifications/openNotification";
import apiClient from "../../actions/api";
import getDate from "../../untils/getDate";
import getDateAndTime from "../../untils/getDateAndTime";
import ChatItem from "../interview/ChatItem";
import useSpecialActions from "../../hooks/useSpecialActions";

// import components
import CallHorizonView from "./CallHorizonView";
import CallCalendarView from "./CallCalendarView";

// import style
import "../../style/calls.scss";

const { TextArea } = Input;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const { RangePicker } = DatePicker;

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const allStatus = [
  {
    color: "gold",
    status: "Waiting",
  },
  {
    color: "",
    status: "Recruiter Phone",
  },
  {
    color: "",
    status: "Recruiter Video",
  },
  {
    color: "rgb(190, 0, 161)",
    status: "HR Call",
  },
  {
    color: "rgb(204, 0, 0)",
    status: "Technical",
  },
  {
    color: "rgb(0, 194, 204)",
    status: "Behavioural",
  },
  {
    color: "rgb(0, 157, 16)",
    status: "Final",
  },
  {
    color: "red",
    status: "Failed",
  },
];

const Calls = ({ title, scope = "personal" }) => {
  const canUseSpecialActions = useSpecialActions();
  // set title
  document.title = title;

  const dateFormat = "YYYY-MM-DD";
  const dateAndTimeFormat = "YYYY-MM-DD HH:mm";
  const user = useSelector((state) => state.auth.user);
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);

  const scrollRef = useRef();
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [searchedData, setSearchedData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchStartDate, setSearchStartDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );
  const [modelUsers, setModelUsers] = useState([]);
  const [isCallerModalOpen, setIsCallerModalOpen] = useState(false);
  const [callerModalLoading, setCallerModalLoading] = useState(false);
  const [editBidId, setEditBidId] = useState("");
  const [editCallerName, setEditCallerName] = useState("");
  const [editStatus, setEditStatus] = useState([]);
  const [editMeetingLink, setEditMeetingLink] = useState("");
  const [editClientAddress, setEditClientAddress] = useState("");
  const [editStartDate, setEditStartDate] = useState(null);
  const [editEndDate, setEditEndDate] = useState(null);
  const [isReviewModalFlag, setIsReviewModalFlag] = useState(false);
  const [editReviewId, setEditReviewId] = useState("");
  const [editReviewStatus, setEditReviewStatus] = useState(0);
  const [editRate, setEditRate] = useState(0);
  const [editDesc, setEditDesc] = useState("");
  const [conversations, setConversations] = useState([]);
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);
  const [callViewMode, setCallViewMode] = useState("Cal");

  const rateValues = ["terrible", "bad", "normal", "good", "wonderful"];

  const radioOptions = [
    { label: "Vertical", value: "Ver", className: "label-1" },
    { label: "Horizontal", value: "Hor", className: "label-2" },
    { label: "Calendar", value: "Cal", className: "label-3" },
  ];

  const handleReviewModalOpen = (item, id) => {
    setEditReviewId(id);
    setEditReviewStatus(item?.status);
    setEditDesc(isEmpty(item?.desc) ? "" : item.desc);
    setEditRate(isEmpty(item?.feeling) ? 0 : item?.feeling);
    setIsReviewModalFlag(true);
  };

  const handleReviewModalCancel = () => {
    setEditReviewId("");
    setEditReviewStatus(0);
    setEditDesc("");
    setEditRate(0);
    setIsReviewModalFlag(false);
  };

  const handleReviewModalSave = async () => {
    if (isEmpty(editReviewId)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please choose correct field.",
      );
      return;
    }

    if (isEmpty(editReviewStatus)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please choose correct status.",
      );
      return;
    }

    if (editRate === 0 && isEmpty(editDesc)) {
      handleReviewModalCancel();
    } else {
      const params = {
        id: editReviewId,
        status: editReviewStatus,
        feeling: editRate,
        desc: editDesc,
      };

      setCallerModalLoading(true);

      await apiClient
        .post("/bids/edit-call-status", params)
        .then(async (res) => {
          if (res.data?.status === "success") {
            openNotification("success", "Success", res.data.msg);
            await getData();
          }
          handleReviewModalCancel();
          setCallerModalLoading(false);
        })
        .catch((err) => {
          if (!isEmpty(err?.response?.data?.errors)) {
            err.response.data.errors.forEach((item) => {
              openNotification("error", "Validation Error!", item.msg);
            });
          }
          handleReviewModalCancel();
          setCallerModalLoading(false);
        });
    }
  };

  const handleCallerModalCancel = () => {
    setEditBidId("");
    setEditCallerName("");
    setEditStatus([]);
    setEditStartDate(null);
    setEditEndDate(null);
    setIsCallerModalOpen(false);
  };

  const handleCallerModalSave = async () => {
    if (isEmpty(editBidId)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please choose correct field.",
      );
      return;
    }

    if (isEmpty(editCallerName)) {
      openNotification("error", "Validation Error!", "Please choose caller.");
      return;
    }

    if (isEmpty(editStatus)) {
      openNotification("error", "Validation Error!", "Please choose status.");
      return;
    }

    if (isEmpty(editStartDate)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please select Start Time.",
      );
      return;
    }

    if (isEmpty(editEndDate)) {
      openNotification("error", "Validation Error!", "Please select End Time.");
      return;
    }

    const params = {
      id: editBidId,
      caller: editCallerName,
      status: editStatus,
      meeting_link: editMeetingLink,
      client_address: editClientAddress,
      start_date: isEmpty(editStartDate)
        ? ""
        : dayjs(editStartDate).tz(timezoneMode.timezoneMode),
      end_date: isEmpty(editEndDate)
        ? ""
        : dayjs(editEndDate).tz(timezoneMode.timezoneMode),
    };

    setCallerModalLoading(true);

    await apiClient
      .post("/bids/edit-by-caller", params)
      .then(async (res) => {
        if (res.data?.status === "success") {
          openNotification("success", "Success", res.data.msg);
          await getData();
        }
        handleCallerModalCancel();
        setCallerModalLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        handleCallerModalCancel();
        setCallerModalLoading(false);
      });
  };

  const handleClickEdit = (record) => {
    setEditBidId(record.id);
    setEditCallerName(isEmpty(record?.caller_id) ? "" : record.caller_id);
    setEditStatus(
      isEmpty(record?.call_log)
        ? []
        : record.call_log.map((item) => item.status),
    );
    setEditMeetingLink(record?.meeting_link);
    setEditClientAddress(record?.client_address);
    setEditStartDate(
      record.status === "Waiting" && record.status === "Failed"
        ? ""
        : isEmpty(record?.start_date)
          ? ""
          : dayjs(
              dayjs(getDateAndTime(record.start_date), dateAndTimeFormat),
            ).tz(timezoneMode.timezoneMode, true),
    );
    setEditEndDate(
      record.status === "Waiting" && record.status === "Failed"
        ? ""
        : isEmpty(record?.end_date)
          ? ""
          : dayjs(dayjs(getDateAndTime(record.end_date), dateAndTimeFormat)).tz(
              timezoneMode.timezoneMode,
              true,
            ),
    );
    setIsCallerModalOpen(true);
  };

  const getColorFromStatus = (status) => {
    const found = allStatus.find((item) => item.status === status);
    return found ? found.color : null;
  };

  const getFormattedDate = (startDate, endDate) => {
    const start = dayjs(startDate).tz(timezoneMode.timezoneMode);
    const end = dayjs(endDate).tz(timezoneMode.timezoneMode);

    // Get the day of the week, month, and date for the start date
    const weekday = start.format("dddd");
    const monthDay = start.format("MMMM D");

    // Get the time in 12-hour format with AM/PM
    const startTime = start.format("hh:mm A");
    const endTime = end.format("hh:mm A");

    return `${weekday}, ${monthDay} ⋅ ${startTime} - ${endTime}`;
  };

  const columns = [
    {
      title: "#",
      key: "no",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Service",
      key: "job_service",
      render: (text, record) => (
        <Tooltip
          placement="top"
          title={
            record.job_url.toLowerCase().includes("devjob")
              ? "https://devjob.ro/en"
              : record.job_url.toLowerCase().includes("djinni")
                ? "https://djinni.co"
                : record.job_url.toLowerCase().includes("eurotechjobs")
                  ? "https://www.eurotechjobs.com/jobs/romania"
                  : record.job_url.toLowerCase().includes("glassdoor")
                    ? "https://glassdoor.ie"
                    : record.job_url.toLowerCase().includes("iamexpat")
                      ? "https://www.iamexpat.nl/career"
                      : record.job_url.toLowerCase().includes("jobright")
                        ? "https://jobright.ai"
                        : record.job_url.toLowerCase().includes("jungle")
                          ? "https://app.welcometothejungle.com"
                          : record.job_url.toLowerCase().includes("justjoin")
                            ? "https://justjoin.it"
                            : record.job_url.toLowerCase().includes("xing")
                              ? "https://xing.com"
                              : record.job_url
                                    .toLowerCase()
                                    .includes("ziprecruiter")
                                ? "https://www.ziprecruiter.com"
                                : record.job_url
                                      .toLowerCase()
                                      .includes("indeed")
                                  ? "https://indeed.com"
                                  : record.job_url
                                        .toLowerCase()
                                        .includes("dice")
                                    ? "https://www.dice.com"
                                    : record.job_url
                                          .toLowerCase()
                                          .includes("cv-library")
                                      ? "https://www.cv-library.co.uk"
                                      : record.job_url
                                            .toLowerCase()
                                            .includes("linkedin")
                                        ? "https://www.linkedin.com"
                                        : record.job_url
                                              .toLowerCase()
                                              .includes("adzuna")
                                          ? "https://www.adzuna.co.uk"
                                          : record.job_url
                                                .toLowerCase()
                                                .includes("ashbyhq")
                                            ? "https://jobs.ashbyhq.com/jobs"
                                            : record.job_url
                                                  .toLowerCase()
                                                  .includes("monster")
                                              ? "https://www.monster.co.uk"
                                              : record.job_url
                                                    .toLowerCase()
                                                    .includes("reed")
                                                ? "https://www.reed.co.uk"
                                                : record.job_url
                                                      .toLowerCase()
                                                      .includes("workable")
                                                  ? "https://jobs.workable.com"
                                                  : record.job_url
                                                        .toLowerCase()
                                                        .includes(
                                                          "euremotejobs",
                                                        )
                                                    ? "https://euremotejobs.com"
                                                    : record.job_url
                                                          .toLowerCase()
                                                          .includes(
                                                            "remoteineurope",
                                                          )
                                                      ? "https://remoteineurope.com"
                                                      : record.job_url
                                                            .toLowerCase()
                                                            .includes(
                                                              "jobserve",
                                                            )
                                                        ? "https://jobserve.com"
                                                        : record.job_url
                                                              .toLowerCase()
                                                              .includes(
                                                                "greenhouse",
                                                              )
                                                          ? "https://www.greenhouse.com"
                                                          : record.job_url
                                                                .toLowerCase()
                                                                .includes(
                                                                  "cryptocurrencyjobs",
                                                                )
                                                            ? "https://cryptocurrencyjobs.co"
                                                            : record.job_url
                                                                  .toLowerCase()
                                                                  .includes(
                                                                    "hexagon",
                                                                  )
                                                              ? "https://careers-manufacturing.hexagon.com"
                                                              : record.job_url
                                                                    .toLowerCase()
                                                                    .includes(
                                                                      "consensys",
                                                                    )
                                                                ? "https://consensys.io"
                                                                : record.job_url
                                                                      .toLowerCase()
                                                                      .includes(
                                                                        "dynamitejobs",
                                                                      )
                                                                  ? "https://dynamitejobs.com"
                                                                  : record.job_url
                                                                        .toLowerCase()
                                                                        .includes(
                                                                          "totaljobs",
                                                                        )
                                                                    ? "https://www.totaljobs.com"
                                                                    : record.job_url
                                                                          .toLowerCase()
                                                                          .includes(
                                                                            "danaher",
                                                                          )
                                                                      ? "https://danaher.jobs"
                                                                      : record.job_url
                                                                            .toLowerCase()
                                                                            .includes(
                                                                              "himalayas",
                                                                            )
                                                                        ? "https://himalayas.app"
                                                                        : record.job_url
                                                                              .toLowerCase()
                                                                              .includes(
                                                                                "nijobs",
                                                                              )
                                                                          ? "https://nijobs.com"
                                                                          : record.job_url
                                                                                .toLowerCase()
                                                                                .includes(
                                                                                  "crypto-careers",
                                                                                )
                                                                            ? "https://crypto-careers.com"
                                                                            : record.job_url
                                                                                  .toLowerCase()
                                                                                  .includes(
                                                                                    "servicenow.dejobs",
                                                                                  )
                                                                              ? "https://servicenow.dejobs.org"
                                                                              : record.job_url
                                                                                    .toLowerCase()
                                                                                    .includes(
                                                                                      "cwjobs",
                                                                                    )
                                                                                ? "https://cwjobs.co.uk"
                                                                                : record.job_url
                                                                                      .toLowerCase()
                                                                                      .includes(
                                                                                        "lever",
                                                                                      )
                                                                                  ? "https://lever.co"
                                                                                  : record.job_url
                                                                                        .toLowerCase()
                                                                                        .includes(
                                                                                          "dover",
                                                                                        )
                                                                                    ? "https://app.dover.com"
                                                                                    : record.job_url
                                                                                          .toLowerCase()
                                                                                          .includes(
                                                                                            "join.com",
                                                                                          )
                                                                                      ? "https://join.com"
                                                                                      : "Unknown Service"
          }
        >
          <img
            className="service-icon"
            src={
              record.job_url.toLowerCase().includes("devjob")
                ? "/assets/image/devjob.png"
                : record.job_url.toLowerCase().includes("djinni")
                  ? "/assets/image/djinni.png"
                  : record.job_url.toLowerCase().includes("eurotechjobs")
                    ? "/assets/image/eurotechjobs.png"
                    : record.job_url.toLowerCase().includes("glassdoor")
                      ? "/assets/image/glassdoor.png"
                      : record.job_url.toLowerCase().includes("iamexpat")
                        ? "/assets/image/iamexpat.png"
                        : record.job_url.toLowerCase().includes("jobright")
                          ? "/assets/image/jobright.png"
                          : record.job_url.toLowerCase().includes("jungle")
                            ? "/assets/image/jungle.png"
                            : record.job_url.toLowerCase().includes("justjoin")
                              ? "/assets/image/justjoin.png"
                              : record.job_url.toLowerCase().includes("xing")
                                ? "/assets/image/xing.png"
                                : record.job_url
                                      .toLowerCase()
                                      .includes("ziprecruiter")
                                  ? "/assets/image/ziprecruiter.png"
                                  : record.job_url
                                        .toLowerCase()
                                        .includes("indeed")
                                    ? "/assets/image/indeed.png"
                                    : record.job_url
                                          .toLowerCase()
                                          .includes("dice")
                                      ? "/assets/image/dice.png"
                                      : record.job_url
                                            .toLowerCase()
                                            .includes("cv-library")
                                        ? "/assets/image/cv-library.png"
                                        : record.job_url
                                              .toLowerCase()
                                              .includes("linkedin")
                                          ? "/assets/image/linkedin.png"
                                          : record.job_url
                                                .toLowerCase()
                                                .includes("adzuna")
                                            ? "/assets/image/adzuna.png"
                                            : record.job_url
                                                  .toLowerCase()
                                                  .includes("ashbyhq")
                                              ? "/assets/image/ashbyhq.png"
                                              : record.job_url
                                                    .toLowerCase()
                                                    .includes("monster")
                                                ? "/assets/image/monster.png"
                                                : record.job_url
                                                      .toLowerCase()
                                                      .includes("reed")
                                                  ? "/assets/image/reed.png"
                                                  : record.job_url
                                                        .toLowerCase()
                                                        .includes("workable")
                                                    ? "/assets/image/workable.png"
                                                    : record.job_url
                                                          .toLowerCase()
                                                          .includes(
                                                            "euremotejobs",
                                                          )
                                                      ? "/assets/image/euremotejobs.png"
                                                      : record.job_url
                                                            .toLowerCase()
                                                            .includes(
                                                              "remoteineurope",
                                                            )
                                                        ? "/assets/image/remoteineurope.png"
                                                        : record.job_url
                                                              .toLowerCase()
                                                              .includes(
                                                                "jobserve",
                                                              )
                                                          ? "/assets/image/jobserve.png"
                                                          : record.job_url
                                                                .toLowerCase()
                                                                .includes(
                                                                  "greenhouse",
                                                                )
                                                            ? "/assets/image/greenhouse.png"
                                                            : record.job_url
                                                                  .toLowerCase()
                                                                  .includes(
                                                                    "cryptocurrencyjobs",
                                                                  )
                                                              ? "/assets/image/cryptocurrencyjobs.png"
                                                              : record.job_url
                                                                    .toLowerCase()
                                                                    .includes(
                                                                      "hexagon",
                                                                    )
                                                                ? "/assets/image/hexagon.png"
                                                                : record.job_url
                                                                      .toLowerCase()
                                                                      .includes(
                                                                        "consensys",
                                                                      )
                                                                  ? "/assets/image/consensys.png"
                                                                  : record.job_url
                                                                        .toLowerCase()
                                                                        .includes(
                                                                          "dynamitejobs",
                                                                        )
                                                                    ? "/assets/image/dynamite.png"
                                                                    : record.job_url
                                                                          .toLowerCase()
                                                                          .includes(
                                                                            "totaljobs",
                                                                          )
                                                                      ? "/assets/image/totaljobs.png"
                                                                      : record.job_url
                                                                            .toLowerCase()
                                                                            .includes(
                                                                              "danaher",
                                                                            )
                                                                        ? "/assets/image/danaher.png"
                                                                        : record.job_url
                                                                              .toLowerCase()
                                                                              .includes(
                                                                                "himalayas",
                                                                              )
                                                                          ? "/assets/image/himalayas.png"
                                                                          : record.job_url
                                                                                .toLowerCase()
                                                                                .includes(
                                                                                  "nijobs",
                                                                                )
                                                                            ? "/assets/image/totaljobs.png"
                                                                            : record.job_url
                                                                                  .toLowerCase()
                                                                                  .includes(
                                                                                    "crypto-careers",
                                                                                  )
                                                                              ? "/assets/image/crypto-careers.jpg"
                                                                              : record.job_url
                                                                                    .toLowerCase()
                                                                                    .includes(
                                                                                      "servicenow.dejobs",
                                                                                    )
                                                                                ? "/assets/image/servicenow.dejobs.png"
                                                                                : record.job_url
                                                                                      .toLowerCase()
                                                                                      .includes(
                                                                                        "cwjobs",
                                                                                      )
                                                                                  ? "/assets/image/cwjobs.png"
                                                                                  : record.job_url
                                                                                        .toLowerCase()
                                                                                        .includes(
                                                                                          "lever",
                                                                                        )
                                                                                    ? "/assets/image/lever.png"
                                                                                    : record.job_url
                                                                                          .toLowerCase()
                                                                                          .includes(
                                                                                            "dover",
                                                                                          )
                                                                                      ? "/assets/image/dover.png"
                                                                                      : record.job_url
                                                                                            .toLowerCase()
                                                                                            .includes(
                                                                                              "join.com",
                                                                                            )
                                                                                        ? "/assets/image/join.com.png"
                                                                                        : "/assets/image/unknown.png"
            }
            alt="job service icon"
          />
        </Tooltip>
      ),
    },
    {
      title: "Bidder",
      key: "bidder",
      dataIndex: "bidder",
      render: (text, record) =>
        !isEmpty(text) ? (
          <Tag
            color={record.bidder_color}
            style={{ color: record.bidder_textcolor }}
          >
            {text}
          </Tag>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
      render: (text, record) => (
        <Tooltip placement="top" title="Download JD">
          {!isEmpty(text) ? (
            <Button
              type="primary"
              onClick={() =>
                handleDownloadJD(record.job_desc, record.company_name)
              }
            >
              {text}
            </Button>
          ) : (
            <span>N/A</span>
          )}
        </Tooltip>
      ),
    },
    {
      title: "Resume",
      dataIndex: "docx_filename",
      key: "docx_filename",
      render: (text, record) => (
        <Tooltip placement="top" title="Download Resume (.docx)">
          {!isEmpty(text, record) ? (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadResume(record.docx_url)}
            />
          ) : (
            <Button
              disabled
              color="danger"
              variant="dashed"
              icon={<StopOutlined />}
            />
          )}
        </Tooltip>
      ),
    },

    {
      title: "Job",
      dataIndex: "job_url",
      key: "job_url",
      render: (text) => (
        <Tooltip placement="top" title={text}>
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(text)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Meeting",
      dataIndex: "meeting_link",
      key: "meeting_link",
      render: (text) => (
        <Tooltip placement="top" title={text}>
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(text)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Caller",
      key: "caller",
      dataIndex: "caller",
      render: (text, record) =>
        !isEmpty(text) ? (
          <Tag
            color={record.caller_color}
            style={{ color: record.caller_textcolor }}
          >
            {text}
          </Tag>
        ) : (
          <span>N/A</span>
        ),
    },
    Table.EXPAND_COLUMN,
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => <Tag color={getColorFromStatus(text)}>{text}</Tag>,
    },
    {
      title: "Bid Time",
      key: "created_at",
      dataIndex: "created_at",
    },
    {
      title: "Call Time",
      key: "start_date",
      dataIndex: "start_date",
      render: (text, record) =>
        !isEmpty(text) ? (
          <span>{getFormattedDate(record.start_date, record.end_date)}</span>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
          {canUseSpecialActions && <Tooltip placement="top" title="Copy Prompt">
            <Button type="primary" onClick={() => handleCopyDetail(record)}>
              <CopyOutlined />
            </Button>
          </Tooltip>}
          <Button type="primary" onClick={() => handleClickEdit(record)}>
            <EditOutlined />
          </Button>
          {canUseSpecialActions && <Button
            type="primary"
            onClick={() => handleViewInterviews(record.id)}
          >
            <WechatOutlined />
          </Button>}
          {canUseSpecialActions && <Button
            color="pink"
            variant="solid"
            onClick={() => {
              const url = `/interview/${record.id}`;
              window.open(url, "_blank");
            }}
          >
            <PlayCircleOutlined />
          </Button>}
        </div>
      ),
    },
  ];

  const handleDownloadPDF = async (pdf_url, filename) => {
    const response = await fetch(`${API_BASE_URL}/job/description/${pdf_url}`, {
      method: "GET",
    });

    if (!response.ok) {
      // handle error
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadResume = (docx_url) => {
    const link = document.createElement("a");
    link.href = `${API_BASE_URL}/job/description/${docx_url}`;
    link.setAttribute("download", "resume.docx");
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownloadJD = async (content, companyname) => {
    const params = {
      content,
    };

    fetch(API_BASE_URL + "/api/bids/download/jd", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${companyname}.txt`; // desired filename
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });
  };

  const handleCopyDetail = async (record) => {
    if (isEmpty(user)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please refresh the page.",
      );
      return;
    }

    setLoading(true);

    const params = {
      id: record.id,
      user_id: user._id,
    };

    await apiClient
      .post("/bids/get-prompt-detail-by-bid", params)
      .then(async (res) => {
        if (res.data.status === "success") {
          handleCopy(res.data.prompt);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
      });
  };

  const handleCancelInterviewsModal = () => {
    setIsInterviewModalOpen(false);
  };

  const handleViewInterviews = async (id) => {
    const params = {
      id,
    };

    setIsInterviewModalOpen(true);
    setLoading(true);

    await apiClient
      .post("/bids/get-detail", params)
      .then((res) => {
        if (res.data.msg !== "success") {
          setConversations([]);
          setLoading(false);
        } else {
          setConversations(res.data.interviews);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        message.success("Copied to clipboard!");
      } else {
        console.warn("Fallback: Copy command was unsuccessful");
        message.error("Failed to copy!");
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
      message.error("Failed to copy!");
    }

    document.body.removeChild(textArea);
  };

  const handleCopy = (copyText) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          message.success("Copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy!");
        });
    } else {
      fallbackCopyTextToClipboard(copyText);
    }
  };

  const handleSearch = async () => {
    const originalStartTime = `${dayjs(searchStartDate).year()}-${String(
      dayjs(searchStartDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchStartDate).date()).padStart(
      2,
      "0",
    )} 00:00:00`;

    const timeStartInUTC = dayjs(originalStartTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeStartInGMTPlus9 = timeStartInUTC.tz(
      timezoneMode.timezoneMode,
      true,
    );

    const originalEndTime = `${dayjs(searchEndDate).year()}-${String(
      dayjs(searchEndDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchEndDate).date()).padStart(
      2,
      "0",
    )} 23:59:59`;
    const timeEndInUTC = dayjs(originalEndTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeEndInGMTPlus9 = timeEndInUTC.tz(timezoneMode.timezoneMode, true);

    const params = {
      user_id: user._id,
      searchStartDate: timeStartInGMTPlus9,
      searchEndDate: timeEndInGMTPlus9,
    };

    setLoading(true);
    await apiClient
      .get(scope === "team" ? "/bids/get-all-calls" : "/bids/get-my-calls", {
        params: {
          startDate: params.searchStartDate,
          endDate: params.searchEndDate,
        },
      })
      .then((res) => {
        if (isEmpty(res.data)) {
          setAllData([]);
          setSearchedData([]);
          setLoading(false);
        } else {
          const tempData = res.data
            .map((item, key) => {
              return {
                key: key + 1,
                id: item._id,
                company_name: item.company_name,
                created_at: getDate(item.date),
                docx_filename: item.docx_filename,
                pdf_filename: item.pdf_filename,
                job_url: item.job_url,
                job_desc: item.job_desc,
                docx_url: item.docx_url,
                pdf_url: item.pdf_url,
                status:
                  item.status === 0
                    ? "Waiting"
                    : item.status === 1
                      ? "Recruiter Phone"
                      : item.status === 2
                        ? "Recruiter Video"
                        : item.status === 7
                          ? "HR Call"
                          : item.status === 3
                            ? "Technical"
                            : item.status === 4
                              ? "Behavioural"
                              : item.status === 5
                                ? "Final"
                                : "Failed",
                caller_avatar: item?.caller?.avatar,
                caller: item?.caller?.name,
                caller_id: item?.caller?._id,
                caller_color: item?.caller?.background_color,
                caller_textcolor: item?.caller?.text_color,
                meeting_link: item?.meeting_link,
                client_address: item?.client_address,
                start_date: item?.start_date,
                end_date: item?.end_date,
                bidder: item?.user.name,
                bidder_id: item?.user._id,
                bidder_color: item?.user?.background_color,
                bidder_textcolor: item?.user?.text_color,
                bidder_avatar: item?.user?.avatar,
                call_log: item?.call_log,
              };
            })
            .sort((a, b) => {
              const dataA = new Date(a.start_date);
              const dataB = new Date(b.start_date);
              return dataB - dataA;
            });

          setAllData(tempData);
          setSearchedData(tempData);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  // get bids data
  const getData = useCallback(async () => {
    const originalStartTime = `${dayjs(searchStartDate).year()}-${String(
      dayjs(searchStartDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchStartDate).date()).padStart(
      2,
      "0",
    )} 00:00:00`;

    const timeStartInUTC = dayjs(originalStartTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeStartInGMTPlus9 = timeStartInUTC.tz(
      timezoneMode.timezoneMode,
      true,
    );

    const originalEndTime = `${dayjs(searchEndDate).year()}-${String(
      dayjs(searchEndDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchEndDate).date()).padStart(
      2,
      "0",
    )} 23:59:59`;
    const timeEndInUTC = dayjs(originalEndTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeEndInGMTPlus9 = timeEndInUTC.tz(timezoneMode.timezoneMode, true);

    const params = {
      user_id: user._id,
      searchStartDate: timeStartInGMTPlus9,
      searchEndDate: timeEndInGMTPlus9,
    };

    setLoading(true);
    await apiClient
      .get(scope === "team" ? "/bids/get-all-calls" : "/bids/get-my-calls", {
        params: {
          startDate: params.searchStartDate,
          endDate: params.searchEndDate,
        },
      })
      .then((res) => {
        if (isEmpty(res.data)) {
          setAllData([]);
          setSearchedData([]);
          setLoading(false);
        } else {
          const tempData = res.data
            .map((item, key) => {
              return {
                key: key + 1,
                id: item._id,
                company_name: item.company_name,
                created_at: getDate(item.date),
                docx_filename: item.docx_filename,
                pdf_filename: item.pdf_filename,
                job_url: item.job_url,
                job_desc: item.job_desc,
                docx_url: item.docx_url,
                pdf_url: item.pdf_url,
                status:
                  item.status === 0
                    ? "Waiting"
                    : item.status === 1
                      ? "Recruiter Phone"
                      : item.status === 2
                        ? "Recruiter Video"
                        : item.status === 7
                          ? "HR Call"
                          : item.status === 3
                            ? "Technical"
                            : item.status === 4
                              ? "Behavioural"
                              : item.status === 5
                                ? "Final"
                                : "Failed",
                caller_avatar: item?.caller?.avatar,
                caller: item?.caller?.name,
                caller_id: item?.caller?._id,
                caller_color: item?.caller?.background_color,
                caller_textcolor: item?.caller?.text_color,
                meeting_link: item?.meeting_link,
                client_address: item?.client_address,
                start_date: item?.start_date,
                end_date: item?.end_date,
                bidder: item?.user.name,
                bidder_id: item?.user._id,
                bidder_color: item?.user?.background_color,
                bidder_textcolor: item?.user?.text_color,
                bidder_avatar: item?.user?.avatar,
                call_log: item?.call_log,
              };
            })
            .sort((a, b) => {
              const dataA = new Date(a.start_date);
              const dataB = new Date(b.start_date);
              return dataB - dataA;
            });

          setAllData(tempData);
          setSearchedData(tempData);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  }, [user, searchStartDate, searchEndDate, scope]);

  const getUsers = async () => {
    await apiClient
      .get("/users/scope-users")
      .then((res) => {
        if (isEmpty(res.data)) {
          setModelUsers([]);
        } else {
          const tempUsers = res.data.map((item, key) => {
            return {
              key,
              value: item._id,
              label: item.name,
            };
          });
          setModelUsers(tempUsers);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
      });
  };

  useEffect(() => {
    if (user && !isEmpty(user._id)) {
      getData();
      getUsers();
    }
  }, [user, getData]);

  useEffect(() => {
    if (!isEmpty(searchText)) {
      const tempSearchedData = allData.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchText.toLowerCase()),
        ),
      );
      setSearchedData(tempSearchedData);
    } else {
      setSearchedData(allData);
    }
  }, [searchText, allData]);

  return (
    <div
      className={`callsSection calls-page ${
        themeMode.themeMode ? "calls-page--dark" : "calls-page--light"
      }`}
    >
      <header className="calls-page-header">
        <div className="calls-page-heading">
          <span className="calls-page-heading-icon">
            <AudioOutlined />
          </span>
          <div>
            <h2 className="title">
              {scope === "team" ? "Team Interviews" : "Interviews"}
            </h2>
            <p>
              {scope === "team"
                ? "Manage team interview schedules, participants, notes, and follow-ups."
                : "Manage your interview schedules, notes, and follow-ups."}
            </p>
          </div>
        </div>
        <div className="calls-page-total">
          <span>{searchedData.length}</span>
          <small>{searchedData.length === 1 ? "meeting" : "meetings"}</small>
        </div>
      </header>

      <div className="search-section">
        <div className="calls-filter-heading">
          <div>
            <FilterOutlined /> Filters
          </div>
          <span>Refine the meetings shown below</span>
        </div>
        <div className="calls-filter-grid">
          <div className="field">
            <div className="label">Search meetings</div>
            <Input
              prefix={<SearchOutlined className="calls-input-icon" />}
              allowClear
              placeholder="Company, role, caller..."
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
          </div>
          <div className="field">
            <div className="label">
              {callViewMode === "Cal" ? "Meeting day" : "Meeting period"}
            </div>
            {callViewMode === "Cal" ? (
              <DatePicker
                value={searchStartDate}
                format={dateFormat}
                disabled={loading}
                allowClear={false}
                onChange={(date) => {
                  const selectedDate = dayjs(date).tz(
                    timezoneMode.timezoneMode,
                    true,
                  );
                  setSearchStartDate(selectedDate);
                  setSearchEndDate(selectedDate);
                }}
              />
            ) : (
              <RangePicker
                value={[searchStartDate, searchEndDate]}
                format={dateFormat}
                disabled={loading}
                onChange={(dates, dateStrings) => {
                  if (dates) {
                    setSearchStartDate(
                      dayjs(dateStrings[0]).tz(timezoneMode.timezoneMode, true),
                    );
                    setSearchEndDate(
                      dayjs(dateStrings[1]).tz(timezoneMode.timezoneMode, true),
                    );
                  } else {
                    setSearchStartDate(
                      dayjs(getDate(dayjs())).tz(
                        timezoneMode.timezoneMode,
                        true,
                      ),
                    );
                    setSearchEndDate(
                      dayjs(getDate(dayjs())).tz(
                        timezoneMode.timezoneMode,
                        true,
                      ),
                    );
                  }
                }}
              />
            )}
          </div>
          <div className="field calls-search-action">
            <Button type="primary" onClick={handleSearch} loading={loading}>
              Search
            </Button>
          </div>
          <div className="calls-result-count">
            <span>{searchedData.length}</span>
            results
          </div>
          <div className="calls-view-switcher">
            <div className="label">View</div>
            <div>
              <Radio.Group
                options={radioOptions}
                onChange={(e) => {
                  const nextView = e.target.value;
                  setCallViewMode(nextView);
                  if (nextView === "Cal") {
                    setSearchEndDate(searchStartDate);
                  }
                }}
                value={callViewMode}
                optionType="button"
                buttonStyle="solid"
              />
            </div>
          </div>
        </div>
      </div>
      <section
        className={`calls-workspace calls-workspace--${callViewMode.toLowerCase()}`}
      >
        {loading && (
          <div
            className="calls-workspace-loading"
            role="status"
            aria-live="polite"
          >
            <div className="calls-workspace-loading-indicator">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 42 }} spin />}
              />
              <span>Loading meetings...</span>
            </div>
          </div>
        )}
        {callViewMode === "Cal" ? (
          <CallCalendarView
            canUseSpecialActions={canUseSpecialActions}
            searchedData={searchedData}
            handleClickEdit={handleClickEdit}
            handleDownloadResume={handleDownloadResume}
            handleDownloadPDF={handleDownloadPDF}
            handleDownloadJD={handleDownloadJD}
            handleCopyDetail={handleCopyDetail}
            handleReviewModalOpen={handleReviewModalOpen}
            allStatus={allStatus}
            rateValues={rateValues}
          />
        ) : callViewMode === "Hor" ? (
          <CallHorizonView
            canUseSpecialActions={canUseSpecialActions}
            searchedData={searchedData}
            handleClickEdit={handleClickEdit}
            handleDownloadResume={handleDownloadResume}
            handleDownloadPDF={handleDownloadPDF}
            handleDownloadJD={handleDownloadJD}
            handleCopyDetail={handleCopyDetail}
            handleReviewModalOpen={handleReviewModalOpen}
            allStatus={allStatus}
            rateValues={rateValues}
          />
        ) : (
          <div className="table-section">
            <div className="calls-table-heading">
              <div>
                <h3>Meeting directory</h3>
                <span>Review and manage every matching interview</span>
              </div>
              <Tag>{searchedData.length} records</Tag>
            </div>
            <Table
              columns={columns}
              dataSource={searchedData}
              loading={loading}
              expandable={{
                expandedRowRender: (record) => (
                  <div className="call-subsection">
                    <List
                      itemLayout="horizontal"
                      dataSource={
                        isEmpty(record?.call_log) ? [] : record.call_log
                      }
                      renderItem={(item, index) => (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <Tag
                                color={getColorFromStatus(
                                  item.status === 0
                                    ? "Waiting"
                                    : item.status === 1
                                      ? "Recruiter Phone"
                                      : item.status === 2
                                        ? "Recruiter Video"
                                        : item.status === 7
                                          ? "HR Call"
                                          : item.status === 3
                                            ? "Technical"
                                            : item.status === 4
                                              ? "Behavioural"
                                              : item.status === 5
                                                ? "Final"
                                                : "Failed",
                                )}
                              >
                                {item.status === 0
                                  ? "Waiting"
                                  : item.status === 1
                                    ? "Recruiter Phone"
                                    : item.status === 2
                                      ? "Recruiter Video"
                                      : item.status === 7
                                        ? "HR Call"
                                        : item.status === 3
                                          ? "Technical"
                                          : item.status === 4
                                            ? "Behavioural"
                                            : item.status === 5
                                              ? "Final"
                                              : "Failed"}
                              </Tag>
                            }
                            description={
                              <>
                                {isEmpty(item?.desc) ? (
                                  <></>
                                ) : (
                                  <div
                                    style={{
                                      marginTop: 8,
                                      color: themeMode.themeMode
                                        ? "#fff"
                                        : "#000",
                                    }}
                                  >
                                    {isEmpty(item?.desc) ? "" : item.desc}
                                  </div>
                                )}
                                {isEmpty(item?.feeling) ? (
                                  <></>
                                ) : (
                                  <div style={{ marginTop: 4 }}>
                                    <Rate
                                      tooltips={rateValues}
                                      value={
                                        isEmpty(item?.feeling)
                                          ? 0
                                          : item.feeling
                                      }
                                      disabled
                                    />
                                  </div>
                                )}
                              </>
                            }
                          />
                          <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() =>
                              handleReviewModalOpen(item, record.id)
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                ),
                rowExpandable: (record) => record.status !== "Waiting",
              }}
            />
          </div>
        )}
      </section>
      <Modal
        rootClassName={`calls-modal ${themeMode.themeMode ? "calls-modal--dark" : ""}`}
        title={
          <>
            <Tag
              color={getColorFromStatus(
                editReviewStatus === 0
                  ? "Waiting"
                  : editReviewStatus === 1
                    ? "Recruiter Phone"
                    : editReviewStatus === 2
                      ? "Recruiter Video"
                      : editReviewStatus === 7
                        ? "HR Call"
                        : editReviewStatus === 3
                          ? "Technical"
                          : editReviewStatus === 4
                            ? "Behavioural"
                            : editReviewStatus === 5
                              ? "Final"
                              : "Failed",
              )}
            >
              {editReviewStatus === 0
                ? "Waiting"
                : editReviewStatus === 1
                  ? "Recruiter Phone"
                  : editReviewStatus === 2
                    ? "Recruiter Video"
                    : editReviewStatus === 7
                      ? "HR Call"
                      : editReviewStatus === 3
                        ? "Technical"
                        : editReviewStatus === 4
                          ? "Behavioural"
                          : editReviewStatus === 5
                            ? "Final"
                            : "Failed"}
            </Tag>
          </>
        }
        open={isReviewModalFlag}
        onOk={handleReviewModalSave}
        onCancel={handleReviewModalCancel}
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={callerModalLoading}
            onClick={handleReviewModalSave}
            icon={<SaveOutlined />}
          >
            SAVE
          </Button>,
        ]}
      >
        <div className="modal-field" style={{ marginTop: 20 }}>
          <label className="label">Your Feeling</label>
          <Rate tooltips={rateValues} onChange={setEditRate} value={editRate} />
        </div>
        <div className="modal-field">
          <label className="label">Follow Up</label>
          <TextArea
            rows={5}
            onChange={(e) => setEditDesc(e.target.value)}
            value={editDesc}
            className="update-textarea"
            style={{ width: "100%" }}
          />
        </div>
      </Modal>
      <Modal
        rootClassName={`calls-modal calls-conversation-modal ${themeMode.themeMode ? "calls-modal--dark" : ""}`}
        title="Interviews"
        open={isInterviewModalOpen}
        onCancel={handleCancelInterviewsModal}
        footer={[]}
        width={{
          xs: "90%",
          sm: "80%",
          md: "70%",
          lg: "60%",
          xl: "50%",
          xxl: "40%",
        }}
      >
        <div className="interview-chat-section">
          {loading ? (
            <div className="interview-loading">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              />
            </div>
          ) : isEmpty(conversations) ? (
            <div className="empty-chat-section">
              <Empty />
            </div>
          ) : (
            conversations.map((item, key) => {
              return (
                <ChatItem
                  key={key}
                  scrollRef={scrollRef}
                  flag={item.flag}
                  desc={item.message}
                />
              );
            })
          )}
        </div>
      </Modal>
      <Modal
        rootClassName={`calls-modal ${themeMode.themeMode ? "calls-modal--dark" : ""}`}
        title="Edit Caller"
        open={isCallerModalOpen}
        onOk={handleCallerModalSave}
        onCancel={handleCallerModalCancel}
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={callerModalLoading}
            onClick={handleCallerModalSave}
            icon={<SaveOutlined />}
          >
            SAVE
          </Button>,
        ]}
      >
        <div className="modal-field">
          <label className="label">Caller</label>
          <Select
            value={editCallerName}
            onChange={(value) => setEditCallerName(value)}
            style={{ width: "100%" }}
            options={[
              {
                value: "",
                label: "Please choose caller",
              },
              ...modelUsers,
            ]}
          />
        </div>
        <div className="modal-field">
          <label className="label">Status</label>
          <Select
            mode="multiple"
            value={editStatus}
            onChange={(value) => setEditStatus(value)}
            style={{ width: "100%" }}
            options={[
              {
                value: 1,
                label: "Recruiter Phone",
              },
              {
                value: 2,
                label: "Recruiter Video",
              },
              {
                value: 7,
                label: "HR Call",
              },
              {
                value: 3,
                label: "Technical",
              },
              {
                value: 4,
                label: "Behavioural",
              },
              {
                value: 5,
                label: "Final",
              },
              {
                value: 6,
                label: "Failed",
              },
            ]}
          />
        </div>
        <div className="modal-field">
          <label className="label">Meeting Link</label>
          <Input
            value={editMeetingLink}
            onChange={(e) => setEditMeetingLink(e.target.value)}
            placeholder="i.e, https://meet.google.com/oxj-rhwy-ino"
          />
        </div>
        <div className="modal-field">
          <label className="label">Client Address</label>
          <Input
            value={editClientAddress}
            placeholder="i.e, emilyhood95@hotmail.com"
            onChange={(e) => setEditClientAddress(e.target.value)}
          />
        </div>
        <div className="modal-field">
          <label className="label">Meeting Time</label>
          <div className="calls-modal-date-range">
            <DatePicker
              showTime
              format={dateAndTimeFormat}
              placeholder="Start Date"
              value={editStartDate}
              onChange={(value, dateString) => {
                const originalTime = dateString;
                const timeInUTC = dayjs(originalTime).tz(
                  timezoneMode.timezoneMode,
                  true,
                );
                setEditStartDate(timeInUTC);
              }}
            />
            <DatePicker
              showTime
              format={dateAndTimeFormat}
              placeholder="End Date"
              value={editEndDate}
              onChange={(value, dateString) => {
                const originalTime = dateString;
                const timeInUTC = dayjs(originalTime).tz(
                  timezoneMode.timezoneMode,
                  true,
                );
                setEditEndDate(timeInUTC);
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Calls;
